import React, { createContext, useContext, useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { formatStorageUrl, supabase } from '@/lib/supabase';
import { formatSong } from '@/utils/songUtils';
import { useNavigate, useLocation } from 'react-router-dom';
import { CATEGORIES, JOURNEY_STAGES } from '@/constants/playlists';
import { Song } from '@/types/player';
import { isSongFavorited } from '@/services/favorites';
import { useAuth } from '@/hooks/useAuth';
import { showToast } from '@/hooks/useToast';
import { useSongs } from '@/hooks/useSongs';
import { cacheService } from '@/services/cacheService';
import { usePlayerState } from '@/hooks/usePlayerState';
import { preloadImages, preloadImage } from '@/services/imagePreloader';
import { PlayerAction, PlayerState } from '@/types/playerStates';
import { usePremiumUpgrade } from '@/context/PremiumUpgradeContext';

import { filterJourneySongs, canAccessJourneySong, FREE_JOURNEY_SONGS } from '@/lib/journeyValidation';
import { playerDebug, playerLog, playerWarn } from '@/utils/playerLogger';

// 🔍 Função helper para logs de debug padronizados do player (mantida para compatibilidade)
function playerDebugLog(action: string, details?: any): void {
  playerDebug(action, details);
}

// Tipos
type PlayerSection = 'category' | 'favorites' | 'journey';

interface GlobalPlayerContextType {
  currentSong: Song;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  queue: Song[];
  currentIndex: number;
  currentSection: PlayerSection;
  currentCategory: string;
  currentPlaylistName: string;
  currentJourneyStageId: string | number | null;
  isShuffled: boolean;
  originalQueue: Song[];
  isSwitchingSong: boolean;
  isTransitioning: boolean;
  isFromHomePage: boolean;
  isFavorited: boolean;
  isNavigatingToPlayer: boolean;
  showMiniPlayer: boolean;
  setIsFavorited: (value: boolean) => void;
  setCurrentSong: (song: Song) => void;
  setIsPlaying: (isPlaying: boolean) => void;
  setCurrentTime: (time: number) => void;
  setDuration: (duration: number) => void;
  setQueue: (queue: Song[]) => void;
  setCurrentIndex: (index: number) => void;
  setCurrentSection: (section: PlayerSection) => void;
  setCurrentCategory: (category: string) => void;
  setCurrentPlaylistName: (name: string) => void;
  setCurrentJourneyStageId: (id: string | number | null) => void;
  setIsShuffled: (isShuffled: boolean) => void;
  setOriginalQueue: (queue: Song[]) => void;
  setIsSwitchingSong: (value: boolean) => void;
  setIsTransitioning: (value: boolean) => void;
  resetPlayerState: (reason?: string) => void;
  play: () => void;
  pause: () => void;
  seek: (time: number) => void;
  playNextSong: () => void;
  playPreviousSong: () => void;
  toggleShuffle: () => void;
  togglePlayPause: () => void;
  loadSong: (songId: number, section: 'category' | 'journey' | 'favorites' | null, category?: string, fromHome?: boolean, journeyStageId?: number | string | null) => Promise<void>;
  loadPlaylist: (songIds: number[], section: 'category' | 'journey' | 'favorites', category?: string, journeyStageId?: number | string | null, initialIndex?: number, source?: string) => Promise<void>;
  stop: () => void;
  canPerformAction: (action: PlayerAction) => boolean;
  playerState: PlayerState;
  emergencyReset: () => void;
}

const AUTOPLAY_DELAY = 3000; // 3 segundos de delay entre músicas

// TTL do cache de playlists: 5 minutos
const PLAYLIST_CACHE_TTL = 5 * 60 * 1000;

// Função para criar chave de cache de playlist
const createPlaylistCacheKey = (
  section: string, 
  category?: string, 
  userId?: string,
  userType?: string
): string => {
  const userInfo = userId ? `${userId}_${userType}` : userId;
  return `playlist_${section}_${category || 'none'}_${userInfo || 'none'}`;
};

export const GlobalPlayerContext = createContext<GlobalPlayerContextType>({
  currentSong: null as unknown as Song,
  isPlaying: false,
  currentTime: 0,
  duration: 0,
  queue: [],
  currentIndex: 0,
  currentSection: 'category',
  currentCategory: '',
  currentPlaylistName: '',
  currentJourneyStageId: null,
  isShuffled: false,
  originalQueue: [],
  isSwitchingSong: false,
  isTransitioning: false,
  isFromHomePage: false,
  isFavorited: false,
  isNavigatingToPlayer: false,
  showMiniPlayer: false,
  setIsFavorited: () => {},
  setCurrentSong: () => {},
  setIsPlaying: () => {},
  setCurrentTime: () => {},
  setDuration: () => {},
  setQueue: () => {},
  setCurrentIndex: () => {},
  setCurrentSection: () => {},
  setCurrentCategory: () => {},
  setCurrentPlaylistName: () => {},
  setCurrentJourneyStageId: () => {},
  setIsShuffled: () => {},
  setOriginalQueue: () => {},
  setIsSwitchingSong: () => {},
  setIsTransitioning: () => {},
  resetPlayerState: () => {},
  play: () => {},
  pause: () => {},
  seek: () => {},
  playNextSong: () => {},
  playPreviousSong: () => {},
  toggleShuffle: () => {},
  togglePlayPause: () => {},
  loadSong: async () => {},
  loadPlaylist: async () => {},
  stop: () => {},
  canPerformAction: () => false,
  playerState: {
    status: 'idle',
    subStatus: {
      isAudioLoading: false,
      isNetworkRequest: false,
      isCacheOperation: false,
      isUserInteraction: false,
    },
    lastAction: 'init',
    timestamp: Date.now(),
  },
  emergencyReset: () => {}
});

// Função para embaralhar array
const shuffleArray = (array: Song[]) => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

// 🔴 CORREÇÃO PROBLEMA 2: Função única para verificar se música pode ser tocada
// Centraliza toda a lógica de premium/grátis em um só lugar
function canPlaySong(song: Song | null, user: any): boolean {
  if (!song) return false;
  
  // Se for usuário premium, pode tocar qualquer música
  if (user?.tipo_de_conta === 'premium') return true;
  
  // Para usuários não-premium (incluindo convidados):
  // Pode tocar se is_free não for explicitamente false
  // Isso inclui: is_free === true, is_free === undefined, is_free === null
  return song.is_free !== false;
}

// Componente interno que lida com a lógica do player
const GlobalPlayerContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { showUpgradeModal } = usePremiumUpgrade();
  
  // OTIMIZADO: Usa o hook centralizado para evitar múltiplas requisições
  const { data: allSongs, getSongsByIds } = useSongs();

  // 🆕 NOVO: Sistema de estados unificado
  const {
    playerState,
    updatePlayerState,
    canPerformAction,
    resetState: resetPlayerState,
    cleanup,
    // Getters para compatibilidade
    isIdle,
    isLoading: isLoadingUnified,
    isPlaying: isPlayingUnified,
    isPaused,
    isNavigating: isNavigatingUnified,
    isTransitioning: isTransitioningUnified,
    hasError,
    canInteract,
    // Estados específicos para compatibilidade
    isLoadingPlaylist: isLoadingPlaylistUnified,
    isLoadingSong: isLoadingSongUnified,
    isSwitchingSong: isSwitchingSongUnified,
    isNavigatingToPlayer: isNavigatingToPlayerUnified,
  } = usePlayerState();

  // Estados do player
  const [currentSong, setCurrentSong] = useState<Song | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [queue, setQueue] = useState<Song[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentSection, setCurrentSection] = useState<PlayerSection>('category');
  const [currentCategory, setCurrentCategory] = useState<string>();
  const [currentPlaylistName, setCurrentPlaylistName] = useState('');
  const [currentJourneyStageId, setCurrentJourneyStageId] = useState<string | number | null>(null);
  const [isShuffled, setIsShuffled] = useState(false);
  const [originalQueue, setOriginalQueue] = useState<Song[]>([]);
  const [isFromHomePage, setIsFromHomePage] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showMiniPlayer, setShowMiniPlayer] = useState(false);

  // Refs
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const currentSongRef = useRef<Song | null>(null);
  const autoplayTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const prevSongRef = useRef<Song | null>(null);
  const isProcessingNextRef = useRef(false);
  const isProcessingPreviousRef = useRef(false);
  const prevUserRef = useRef<typeof user>(user);
  // 🔍 DEBUG: Refs para acessar valores atualizados nos event listeners
  const currentIndexRef = useRef(currentIndex);
  const queueRef = useRef(queue);
  const lastRequestedSongIdRef = useRef<number | null>(null);

  // 🔄 TRANSIÇÃO: Estados antigos mantidos para compatibilidade gradual
  // Estes serão sincronizados com o estado unificado
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isSwitchingSong, setIsSwitchingSong] = useState(false);
  const [isNavigatingToPlayer, setIsNavigatingToPlayer] = useState(false);
  const [isLoadingSong, setIsLoadingSong] = useState(false);
  const [isLoadingPlaylist, setIsLoadingPlaylist] = useState(false);
  const [lastProcessedSongId, setLastProcessedSongId] = useState<string | null>(null);

  // 🔄 SINCRONIZAÇÃO: Mantém estados antigos sincronizados com o novo sistema
  useEffect(() => {
    setIsTransitioning(isTransitioningUnified);
    setIsSwitchingSong(isSwitchingSongUnified);
    setIsNavigatingToPlayer(isNavigatingToPlayerUnified);
    setIsLoadingSong(isLoadingSongUnified);
    setIsLoadingPlaylist(isLoadingPlaylistUnified);
  }, [isTransitioningUnified, isSwitchingSongUnified, isNavigatingToPlayerUnified, isLoadingSongUnified, isLoadingPlaylistUnified]);

  // 🔍 DEBUG: Loga todas as transições de estado do player
  useEffect(() => {
    playerDebugLog('state-transition', {
      status: playerState.status,
      lastAction: playerState.lastAction,
      subStatus: playerState.subStatus,
      error: playerState.error,
      songId: currentSong?.id,
      currentIndex,
      queueLength: queue.length,
      currentTime: audioRef.current?.currentTime,
      duration: audioRef.current?.duration
    });
  }, [playerState.status, playerState.lastAction, currentSong?.id, currentIndex, queue.length]);

  // Cleanup no unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  // 🚀 DESABILITADO TEMPORARIAMENTE: Preload causando lentidão
  useEffect(() => {
    if (currentSong?.capa_url) {
    }
  }, [currentSong?.id]);

  // Funções de fade
  const fadeOut = useCallback((duration = 300) => {
    return new Promise<void>((resolve) => {
      if (!audioRef.current) {
        resolve();
        return;
      }

      const audio = audioRef.current;
      const startVolume = audio.volume;
      const startTime = Date.now();

      const fade = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        if (audio) {
          audio.volume = startVolume * (1 - progress);
          
          if (progress < 1) {
            requestAnimationFrame(fade);
          } else {
            audio.volume = 0;
            resolve();
          }
        }
      };

      fade();
    });
  }, []);

  const fadeIn = useCallback((duration = 300) => {
    return new Promise<void>((resolve) => {
      if (!audioRef.current) {
        resolve();
        return;
      }

      const audio = audioRef.current;
      const startVolume = audio.volume;
      const targetVolume = 1;
      const startTime = Date.now();

      const fade = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        if (audio) {
          audio.volume = startVolume + (targetVolume - startVolume) * progress;
          
          if (progress < 1) {
            requestAnimationFrame(fade);
          } else {
            audio.volume = targetVolume;
            resolve();
          }
        }
      };

      fade();
    });
  }, []);

  // 🔴 CORREÇÃO PROBLEMA 7: Simplifica funções de controle de reprodução
  // Remove fade complexo que pode causar problemas de timing
  const play = useCallback(async () => {
    if (!audioRef.current || !currentSong) {
      playerDebugLog('play', { error: 'audioRef ou currentSong não disponível' });
      return;
    }

    // 🆕 NOVO: Verifica se pode executar ação
    if (!canPerformAction('play-song')) {
      playerWarn('[PlayerState] Não é possível tocar música no estado atual:', playerState.status);
      playerDebugLog('play', { error: 'ação não permitida', status: playerState.status });
      return;
    }

    playerDebugLog('play', {
      songId: currentSong.id,
      currentIndex,
      queueLength: queue.length,
      currentTime: audioRef.current?.currentTime,
      duration: audioRef.current?.duration,
      readyState: audioRef.current?.readyState
    });

    // 🆕 NOVO: Atualiza estado unificado
    updatePlayerState('buffering', 'play-start', { isUserInteraction: true });

    try {
      // Verificar se o áudio está pronto
      if (audioRef.current.readyState < 3) {
        await new Promise((resolve) => {
          if (audioRef.current) {
            audioRef.current.addEventListener('canplaythrough', resolve, { once: true });
          }
        });
      }

      // 🔴 CORREÇÃO: Remove fade in complexo - volume direto
      audioRef.current.volume = 1;
      await audioRef.current.play();
      setIsPlaying(true);
      
      // 🆕 NOVO: Atualiza para estado de reprodução
      updatePlayerState('playing', 'play-success');
      
      playerDebugLog('play-success', {
        songId: currentSong.id,
        currentIndex,
        queueLength: queue.length
      });

    } catch (error) {
      console.error('Error playing audio:', error);
      setIsPlaying(false);
      
      // 🆕 NOVO: Atualiza para estado de erro
      updatePlayerState('error', 'play-failed', {}, error instanceof Error ? error.message : 'Erro desconhecido');
      
      playerDebugLog('play-failed', {
        songId: currentSong.id,
        error: error instanceof Error ? error.message : 'Erro desconhecido'
      });
    }
  }, [currentSong, canPerformAction, playerState.status, updatePlayerState, currentIndex, queue.length]);

  const pause = useCallback(async () => {
    if (!audioRef.current) {
      playerDebugLog('pause', { error: 'audioRef não disponível' });
      return;
    }
    
    // 🆕 NOVO: Verifica se pode pausar
    if (!canPerformAction('pause')) {
      playerWarn('[PlayerState] Não é possível pausar no estado atual:', playerState.status);
      playerDebugLog('pause', { error: 'ação não permitida', status: playerState.status });
      return;
    }

    playerDebugLog('pause', {
      songId: currentSong?.id,
      currentIndex,
      queueLength: queue.length,
      currentTime: audioRef.current?.currentTime,
      duration: audioRef.current?.duration
    });

    // 🆕 NOVO: Atualiza estado unificado
    updatePlayerState('paused', 'pause-start', { isUserInteraction: true });
    
    // 🔴 CORREÇÃO: Remove fade out complexo - pausa direta
    audioRef.current.pause();
    setIsPlaying(false);
    
    playerDebugLog('pause-success', {
      songId: currentSong?.id,
      currentIndex,
      queueLength: queue.length
    });

  }, [canPerformAction, playerState.status, updatePlayerState, currentSong?.id, currentIndex, queue.length]);

  const stop = useCallback(() => {
    playerDebugLog('stop', {
      currentSongId: currentSong?.id,
      currentIndex,
      queueLength: queue.length,
      currentTime: audioRef.current?.currentTime,
      duration: audioRef.current?.duration,
      status: playerState.status
    });

    if (!canPerformAction('stop')) {
      playerDebugLog('stop', { error: 'ação não permitida', status: playerState.status });
      return;
    }

    // Atualiza o estado antes de manipular o áudio
    setIsPlaying(false);
    setCurrentSong(null);
    setQueue([]);
    setCurrentIndex(0);
    setShowMiniPlayer(false);
    
    // Limpa o áudio de forma silenciosa
    if (audioRef.current) {
      const audio = audioRef.current;
      
      try {
        // Primeiro pausa e reseta o tempo
        audio.pause();
        audio.currentTime = 0;
        
        // Remove os event listeners temporariamente
        const originalOnError = audio.onerror;
        audio.onerror = null;
        
        // Limpa a fonte
        audio.src = '';
        audio.removeAttribute('src');
        audio.load();
        
        // Restaura o handler de erro original
        setTimeout(() => {
          audio.onerror = originalOnError;
        }, 0);
      } catch (error) {
        // Ignora erros de limpeza silenciosamente
      }
    }
    
    // Reseta outros estados importantes
    setCurrentSection('category');
    setCurrentCategory(undefined);
    setCurrentPlaylistName('');
    setIsShuffled(false);
    setOriginalQueue([]);
    setIsTransitioning(false);
    setIsSwitchingSong(false);
    
    // Reseta locks de navegação
    navigationLockRef.current = false;
    playSongLockRef.current = false;
    currentPlayingSongId.current = null;
    lastNavigationTime.current = 0;
    
    updatePlayerState('idle', 'user-stop');
    
    playerDebugLog('stop-success', {
      status: 'idle'
    });
  }, [canPerformAction, updatePlayerState, currentSong?.id, currentIndex, queue.length, playerState.status]);

  // 🆕 NOVO: Função de emergência para resetar completamente o player
  const emergencyReset = useCallback(() => {
    playerWarn('[Player] Executando reset de emergência devido a travamento');
    
    // Para imediatamente qualquer áudio
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.src = '';
        audioRef.current.load();
      } catch (error) {
        console.error('[Player] Erro ao resetar áudio:', error);
      }
    }
    
    // Reseta todos os locks
    navigationLockRef.current = false;
    playSongLockRef.current = false;
    currentPlayingSongId.current = null;
    lastNavigationTime.current = 0;
    
    // Reseta estados
    setIsPlaying(false);
    setCurrentSong(null);
    setQueue([]);
    setCurrentIndex(0);
    setIsTransitioning(false);
    setIsSwitchingSong(false);
    setShowMiniPlayer(false);
    
    // Reseta estado unificado
    updatePlayerState('idle', 'emergency-reset', { 
      isUserInteraction: true 
    });
    
  }, [updatePlayerState]);

  // 🆕 NOVO: Expor função de emergência globalmente para debugging
  useEffect(() => {
    (window as any).__emergencyPlayerReset = emergencyReset;
    return () => {
      delete (window as any).__emergencyPlayerReset;
    };
  }, [emergencyReset]);

  // 🆕 NOVO: Detector de travamento - reseta automaticamente se locks ficarem ativos muito tempo
  useEffect(() => {
    const DEADLOCK_TIMEOUT = 10000; // 10 segundos
    
    const checkForDeadlock = () => {
      const now = Date.now();
      
      // Verifica se navigation lock está ativo há muito tempo
      if (navigationLockRef.current && (now - lastNavigationTime.current) > DEADLOCK_TIMEOUT) {
        emergencyReset();
        return;
      }
      
      // Verifica se playSong lock está ativo há muito tempo
      if (playSongLockRef.current && (now - lastNavigationTime.current) > DEADLOCK_TIMEOUT) {
        emergencyReset();
        return;
      }
    };
    
    const deadlockInterval = setInterval(checkForDeadlock, 5000); // Verifica a cada 5 segundos
    
    return () => {
      clearInterval(deadlockInterval);
    };
  }, [emergencyReset]);

  const togglePlayPause = useCallback(() => {
    if (!canPerformAction('play-song') && !canPerformAction('pause')) {
      return;
    }

    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, play, pause, canPerformAction, playerState.status]);

  // 🆕 NOVO: Sistema de proteção contra spam de operações
  const lastNavigationTime = useRef<number>(0);
  const navigationLockRef = useRef<boolean>(false);
  const MIN_NAVIGATION_INTERVAL = 300; // Mínimo 300ms entre navegações
  const MAX_CONCURRENT_OPERATIONS = 1; // Máximo 1 operação simultânea
  
  const canNavigate = useCallback(() => {
    const now = Date.now();
    const timeSinceLastNavigation = now - lastNavigationTime.current;
    
    // Verifica se já existe operação em andamento
    if (navigationLockRef.current) {
      // Operação de navegação já em andamento
      return false;
    }
    
    // Verifica intervalo mínimo entre navegações
    if (timeSinceLastNavigation < MIN_NAVIGATION_INTERVAL) {
      // Navegação muito rápida - aguardando intervalo
      return false;
    }
    
    return true;
  }, []);

  const withNavigationLock = useCallback(async (operation: () => Promise<void> | void) => {
    if (!canNavigate()) {
      return;
    }
    
    try {
      navigationLockRef.current = true;
      lastNavigationTime.current = Date.now();
      
      await operation();
    } catch (error) {
      console.error('[Navigation] Erro durante operação:', error);
    } finally {
      // Libera o lock após um delay para evitar spam
      setTimeout(() => {
        navigationLockRef.current = false;
      }, 100);
    }
  }, [canNavigate]);

  // 🆕 NOVO: Função auxiliar para re-embaralhar a queue mantendo a música atual
  const reshuffleQueueFromIndex = useCallback((currentIdx: number) => {
    if (!isShuffled || queue.length <= 1) return;

    const beforeIds = queue.map(s => s.id);
    const currentSong = queue[currentIdx];
    const remainingSongs = queue.filter((_, idx) => idx !== currentIdx);
    const shuffledRemaining = shuffleArray(remainingSongs);
    const newQueue = [currentSong, ...shuffledRemaining];
    const afterIds = newQueue.map(s => s.id);
    
    setQueue(newQueue);
    setCurrentIndex(0);
  }, [isShuffled, queue]);

  // 🆕 NOVO: Função centralizada para encontrar a próxima música tocável.
  const findNextPlayableSongIndex = useCallback((direction: 'next' | 'previous'): number | null => {
    if (queue.length === 0) return null;

    let searchIndex = currentIndex;
    const totalSongs = queue.length;

    for (let i = 0; i < totalSongs; i++) {
      if (direction === 'next') {
        searchIndex = (searchIndex + 1) % totalSongs;
      } else {
        searchIndex = (searchIndex - 1 + totalSongs) % totalSongs;
      }

      // Evita loop infinito se a música atual for a única tocável
      if (searchIndex === currentIndex && i > 0) {
         const currentSongPlayable = canPlaySong(queue[currentIndex], user);
         if(currentSongPlayable) return null;
      }

      const song = queue[searchIndex];

      if (song) {
        // Para favoritos, validação mais simples - se está na lista de favoritos, pode tocar
        if (currentSection === 'favorites') {
          // Se é favorito, pode tocar (já passou pela validação premium ao adicionar aos favoritos)
          return searchIndex;
        } else if (canPlaySong(song, user)) {
           // Lógica específica para a jornada
           if (currentSection === 'journey') {
             if (canAccessJourneySong(Number(song.id), user?.tipo_de_conta)) {
               return searchIndex;
             }
           } else {
             return searchIndex;
           }
        }
      }
    }
    
    // Se nenhuma música tocável for encontrada
    return null;
  }, [queue, currentIndex, user, currentSection]);

  const playNextSong = useCallback(() => {
    playerDebugLog('next', {
      currentSongId: currentSong?.id,
      currentIndex,
      queueLength: queue.length,
      currentTime: audioRef.current?.currentTime,
      duration: audioRef.current?.duration
    });

    withNavigationLock(async () => {
      if (!canPerformAction('next')) {
        playerDebugLog('next', { error: 'ação não permitida', status: playerState.status });
        return;
      }

      const fromIndex = currentIndex;
      const nextIndex = findNextPlayableSongIndex('next');

      if (nextIndex !== null) {
        const nextSong = queue[nextIndex];
        
        playerDebugLog('next-switching', {
          fromIndex: currentIndex,
          toIndex: nextIndex,
          fromSongId: currentSong?.id,
          toSongId: nextSong.id
        });
        // 🔴 CORREÇÃO 1: APENAS next/previous podem atualizar currentIndex
        setCurrentIndex(nextIndex);
        // 🟡 CORREÇÃO 3: Atualiza lastAction para refletir ação real
        updatePlayerState('switching-context', 'next', { isUserInteraction: true });
        try {
          await playSong(nextSong);
          // 🟡 CORREÇÃO 3: Restaura lastAction após playSong executar
          updatePlayerState('playing', 'next');
          playerDebugLog('next-success', {
            songId: nextSong.id,
            currentIndex: nextIndex,
            queueLength: queue.length
          });
        } catch (error) {
          console.error('[PlayNextSong] Erro ao reproduzir música:', error);
          showToast('Não foi possível tocar a próxima música.', 'error');
          playerDebugLog('next-failed', {
            error: error instanceof Error ? error.message : 'Erro desconhecido',
            nextIndex
          });
        }
      } else {
        // Opcional: Parar o player ou apenas mostrar um aviso
        showToast('Não há mais músicas disponíveis para tocar nesta lista.', 'info');
        playerDebugLog('next', { error: 'nenhuma música disponível', currentIndex, queueLength: queue.length });
        // Se a música atual não puder ser tocada, pare o player.
        if(!canPlaySong(currentSong, user)) {
          stop();
        }
      }
    });
  }, [queue, user, currentSong, canPerformAction, withNavigationLock, findNextPlayableSongIndex, stop, currentIndex, playerState.status, isShuffled]);

  const playPreviousSong = useCallback(() => {
    playerDebugLog('previous', {
      currentSongId: currentSong?.id,
      currentIndex,
      queueLength: queue.length,
      currentTime: audioRef.current?.currentTime,
      duration: audioRef.current?.duration
    });

    withNavigationLock(async () => {
      if (!canPerformAction('switch-song')) {
        playerDebugLog('previous', { error: 'ação não permitida', status: playerState.status });
        return;
      }
      
      const fromIndex = currentIndex;
      const prevIndex = findNextPlayableSongIndex('previous');

      if (prevIndex !== null) {
        const prevSong = queue[prevIndex];
        
        playerDebugLog('previous-switching', {
          fromIndex: currentIndex,
          toIndex: prevIndex,
          fromSongId: currentSong?.id,
          toSongId: prevSong.id
        });
        // 🔴 CORREÇÃO 1: APENAS next/previous podem atualizar currentIndex
        setCurrentIndex(prevIndex);
        // 🟡 CORREÇÃO 3: Atualiza lastAction para refletir ação real
        updatePlayerState('switching-context', 'previous', { isUserInteraction: true });
        try {
          await playSong(prevSong);
          // 🟡 CORREÇÃO 3: Restaura lastAction após playSong executar
          updatePlayerState('playing', 'previous');
          playerDebugLog('previous-success', {
            songId: prevSong.id,
            currentIndex: prevIndex,
            queueLength: queue.length
          });
        } catch (error) {
          console.error('[PlayPreviousSong] Erro ao reproduzir música:', error);
          showToast('Não foi possível tocar a música anterior.', 'error');
          playerDebugLog('previous-failed', {
            error: error instanceof Error ? error.message : 'Erro desconhecido',
            prevIndex
          });
        }
      } else {
        showToast('Não há mais músicas disponíveis para tocar nesta lista.', 'info');
        playerDebugLog('previous', { error: 'nenhuma música disponível', currentIndex, queueLength: queue.length });
      }
    });
  }, [queue, user, canPerformAction, withNavigationLock, findNextPlayableSongIndex, currentSong?.id, currentIndex, playerState.status, isShuffled]);

  // Refs para garantir que os listeners sempre usem as versões mais recentes das funções
  const playNextSongRef = useRef(playNextSong);
  const playPreviousSongRef = useRef(playPreviousSong);
  useEffect(() => { playNextSongRef.current = playNextSong; }, [playNextSong]);
  useEffect(() => { playPreviousSongRef.current = playPreviousSong; }, [playPreviousSong]);
  useEffect(() => { currentSongRef.current = currentSong; }, [currentSong]);
  // 🔍 DEBUG: Atualiza refs para event listeners
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);
  useEffect(() => { queueRef.current = queue; }, [queue]);

  // Handler para quando a música termina
  // 🟡 CORREÇÃO 3: endedHandler deve SEMPRE chamar next() sem locks ou validações extras
  const endedHandler = useCallback(() => {
    playerDebugLog('ended-handler', {
      songId: currentSongRef.current?.id,
      currentIndex: currentIndexRef.current,
      queueLength: queueRef.current.length
    });
    // Chama next() diretamente, sem locks ou validações
    playNextSongRef.current();
  }, []);

  // Usa a função formatSong centralizada
  // (formatSong já está importado de @/utils/songUtils)

  // Função para buscar músicas sem cache
  const fetchSongs = useCallback(async (songIds: number[]): Promise<Song[]> => {
    try {
      // Usa o sistema centralizado (async)
      const songs = await getSongsByIds(songIds);
      
      if (!songs || songs.length === 0) {
        // Nenhuma música encontrada para os IDs fornecidos
        return [];
      }

      // Formata as músicas para o formato esperado usando função centralizada
      const formattedSongs = songs.map(song => formatSong(song));

      

      return formattedSongs;
    } catch (error) {
      // Erro ao buscar músicas
      return [];
    }
  }, [getSongsByIds, preloadImages]);

  // Função para limpar o timeout do autoplay
  const clearAutoplayTimeout = () => {
    if (autoplayTimeoutRef.current) {
      clearTimeout(autoplayTimeoutRef.current);
    }
  };

  // Constante global para timeout
  const AUDIO_TIMEOUT = 5000; // 5 segundos
  
  // 🆕 NOVO: Sistema de proteção contra operações concorrentes em playSong
  const playSongLockRef = useRef<boolean>(false);
  const currentPlayingSongId = useRef<string | null>(null);

  const playSong = async (song: Song) => {
    const startTime = performance.now();
    
    playerDebugLog('playSong', {
      songId: song.id,
      currentSongId: currentSong?.id,
      currentTime: audioRef.current?.currentTime,
      duration: audioRef.current?.duration
    });
    
    // 🆕 NOVO: Proteção contra operações concorrentes
    if (playSongLockRef.current) {
      playerDebugLog('playSong', { error: 'lock ativo - operação concorrente bloqueada', songId: song.id });
      return false;
    }
    
    if (!canPlaySong(song, user)) {
      playerDebugLog('playSong', { error: 'música não pode ser tocada', songId: song.id });
      return false;
    }
    
    try {
      // 🆕 NOVO: Ativa o lock para esta operação
      playSongLockRef.current = true;
      currentPlayingSongId.current = song.descricao;
      setIsSwitchingSong(true);
      setIsTransitioning(true);

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.volume = 1;
      }

      setCurrentTime(0); 
      setProgress(0);    
      setCurrentSong(song);

      // 🔴 CORREÇÃO 1: playSong NÃO deve calcular ou setar currentIndex
      // O índice deve ser gerenciado apenas por next/previous e loadPlaylist

      if (audioRef.current) {
        const newSrc = formatStorageUrl(song.audio_url);
        
        let sourceChanged = false;
        if (audioRef.current.src !== newSrc) {
          audioRef.current.src = newSrc;
          sourceChanged = true;
        }
        
        audioRef.current.currentTime = 0;
        audioRef.current.volume = 1;
        
        if (sourceChanged) {
          audioRef.current.load(); 
        }
        
        // 🟠 CORREÇÃO 2: Aguardar canplaythrough antes de chamar play()
        await new Promise<void>((resolve, reject) => {
          const timeoutId = setTimeout(() => {
            audioRef.current?.removeEventListener('canplaythrough', onCanPlayThrough);
            audioRef.current?.removeEventListener('error', onError);
            reject(new Error('Timeout ao carregar áudio'));
          }, AUDIO_TIMEOUT);
          
          const onCanPlayThrough = () => {
            clearTimeout(timeoutId);
            audioRef.current?.removeEventListener('canplaythrough', onCanPlayThrough);
            audioRef.current?.removeEventListener('error', onError);
            resolve();
          };
          
          const onError = (e: Event) => {
            clearTimeout(timeoutId);
            audioRef.current?.removeEventListener('canplaythrough', onCanPlayThrough);
            audioRef.current?.removeEventListener('error', onError);
            reject(new Error('Erro ao carregar áudio'));
          };
          
          if (audioRef.current) {
            // Se já está pronto, resolve imediatamente
            if (audioRef.current.readyState >= 3) {
              clearTimeout(timeoutId);
              resolve();
            } else {
              audioRef.current.addEventListener('canplaythrough', onCanPlayThrough, { once: true });
              audioRef.current.addEventListener('error', onError, { once: true });
            }
          }
        });

        try {
          // Agora sim, pode chamar play() com segurança
          await audioRef.current.play();
          const duration = performance.now() - startTime;
          setIsPlaying(true);
          // 🟢 CORREÇÃO 3: Atualiza lastAction para refletir ação real
          updatePlayerState('playing', 'playSong');
          
          playerDebugLog('playSong-success', {
            songId: song.id,
            duration: duration
          });
          
        } catch (playError) {
          const duration = performance.now() - startTime;
          setIsPlaying(false);
          // 🟢 CORREÇÃO 3: Atualiza lastAction para refletir ação real
          updatePlayerState('error', 'playSong');
          
          playerDebugLog('playSong-failed', {
            songId: song.id,
            error: playError instanceof Error ? playError.message : 'Erro desconhecido',
            duration: duration
          });
          
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            audioRef.current.src = '';
            audioRef.current.load();
          }
        }
      }
        
      return true;
    } catch (error) {
      const duration = performance.now() - startTime;
      
      playerDebugLog('playSong-error', {
        songId: song.id,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
        duration: duration
      });
      
      setIsPlaying(false);
      return false;
    } finally {
      setIsTransitioning(false);
      setIsSwitchingSong(false);
      // 🆕 NOVO: Libera o lock sempre no final
      playSongLockRef.current = false;
      currentPlayingSongId.current = null;
    }
  };

  // Event handlers
  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      setProgress((audioRef.current.currentTime / audioRef.current.duration) * 100);
    }
  }, []);
      
  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  }, []);

  const loadSong = useCallback(async (songId: number, section: 'category' | 'journey' | 'favorites' | null, category?: string, fromHome?: boolean, journeyStageId?: number | string | null) => {
    const startTime = performance.now();
    
    playerDebugLog('loadSong', {
      songId,
      section,
      category,
      fromHome,
      journeyStageId,
      currentSongId: currentSong?.id,
      currentIndex,
      queueLength: queue.length,
      status: playerState.status
    });
    
    // OTIMIZADO: Evita carregamento duplicado da mesma música
    const songIdStr = String(songId);
    if (isLoadingSong || (lastProcessedSongId === songIdStr && currentSong?.id === songIdStr)) {
      // LoadSong cancelado: já carregando ou música atual
      playerDebugLog('loadSong', { error: 'cancelado - já carregando ou música atual', songId });
      return;
    }

    // 🔴 CORREÇÃO PROBLEMA 3: Simplifica verificações de estado
    if (isTransitioning) {
      // LoadSong cancelado: em transição
      playerDebugLog('loadSong', { error: 'cancelado - em transição', songId });
      return;
    }

    setIsLoadingSong(true);
    setLastProcessedSongId(songIdStr);

    // Usa AUDIO_TIMEOUT padronizado
    let loadingTimeout: NodeJS.Timeout;

    try {
      // Buscando música por ID
      // Busca a música
      const song = await getSongsByIds([songId]).then(songs => songs[0]);
      if (!song) {
        // Música não encontrada
        throw new Error('Música não encontrada');
      }
      const searchDuration = performance.now() - startTime;
      // Música encontrada com sucesso
    } catch (error) {
      const duration = performance.now() - startTime;
      // Erro ao buscar música
      throw error;
    }

    const resetLoadingState = () => {
      setIsTransitioning(false);
      setIsSwitchingSong(false);
      setIsLoadingSong(false);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.src = '';
      }
    };

    const handleTimeout = () => {
      resetLoadingState();
    };

    // Usa AUDIO_TIMEOUT padronizado
    loadingTimeout = setTimeout(handleTimeout, AUDIO_TIMEOUT);

    try {
      setIsTransitioning(true);
      setIsSwitchingSong(true);
      
      setCurrentTime(0); 
      setProgress(0);    
      
      // Limpa apenas o player de áudio, mas mantém currentSong
      if (audioRef.current) {
        try {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          audioRef.current.src = '';
          audioRef.current.load();
          await new Promise(resolve => setTimeout(resolve, 50));
        } catch (error) {
          // Ignora erros de limpeza do player
        }
      }
      
      // OTIMIZADO: Busca a música com cache agressivo
      let songToLoad: Song | undefined;
      const songCacheKey = `single_song_${songId}`;
      
      if (!songToLoad) {
        // Verifica se já temos a música carregada
        if (allSongs && allSongs.length > 0) {
          const foundInAllSongs = allSongs.find(s => Number(s.id) === Number(songId));
          if (foundInAllSongs) {
            songToLoad = formatSong(foundInAllSongs);
          }
        }
      }
      
      if (!songToLoad) {
        // Busca a música diretamente
        const { data: song } = await supabase
          .from('songs')
          .select('*')
          .eq('id', songId)
          .single();

        if (!song) {
          throw new Error('Música não encontrada');
        }

        songToLoad = formatSong(song);
      }

      if (!songToLoad) {
        throw new Error('Não foi possível carregar a música');
      }

      setCurrentSong(songToLoad);
      setIsFromHomePage(!!fromHome);

      let finalQueue: Song[] = [];
      let finalIndex = 0;

      // Limpa o estado do modo aleatório APENAS se uma música for carregada diretamente (section === null)
      if (isShuffled && section === null) {
        setIsShuffled(false);
        setOriginalQueue([]);
      }
      
      // Se a seção for nula (ex: deep link), cria uma fila simples
      if (section === null) {
        finalQueue = [songToLoad];
        finalIndex = 0;
        setCurrentSection('category');
        setCurrentCategory(undefined);
        setCurrentJourneyStageId(null);
        setCurrentPlaylistName(''); // Limpa o nome da playlist
      } else {
        // Lógica existente para quando uma seção é fornecida
        setCurrentSection(section);
        setCurrentCategory(category);
        if (section === 'journey' && journeyStageId) {
          setCurrentJourneyStageId(journeyStageId);
        } else if (section !== 'journey') {
          setCurrentJourneyStageId(null);
        }

        // Carrega IDs das músicas baseado na seção
        let songIdsForContext: number[] = [];
        if (section === 'category' && category) {
          songIdsForContext = CATEGORIES[category]?.songIds || [];
        } else if (section === 'journey') {
          songIdsForContext = JOURNEY_STAGES.flatMap(stage => stage.songIds);
        } else if (section === 'favorites') {
          if (!user?.id) {
            showToast('Você precisa estar logado para ouvir seus favoritos.', 'error');
            return;
          }
          const { data, error } = await supabase
            .from('favoritos')
            .select('songs(*)')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false });

          if (error) throw error;
          
          songIdsForContext = (data || [])
            .map((item: any) => item.songs)
            .filter(Boolean);
        }

        if (songIdsForContext.length > 0) {
          const fetchedSongs = await fetchSongs(songIdsForContext);
          let filteredSongs = fetchedSongs;

          // VALIDAÇÃO PREMIUM específica por seção
          if (section === 'journey' && user?.tipo_de_conta !== 'premium') {
            // JORNADA: apenas IDs 22, 23, 453 são gratuitos
            filteredSongs = filterJourneySongs(fetchedSongs, user?.tipo_de_conta);
          } else if (section !== 'favorites' && user?.tipo_de_conta !== 'premium') {
            // OUTRAS SEÇÕES: usa validação normal
            filteredSongs = fetchedSongs.filter(s => canPlaySong(s, user));
          }
          
          const indexInFetched = filteredSongs.findIndex(s => String(s.id) === String(songToLoad.id));

          if (indexInFetched !== -1) {
            finalQueue = filteredSongs;
            finalIndex = indexInFetched;
          } else {
            finalQueue = [songToLoad];
            finalIndex = 0;
          }
        } else {
          finalQueue = [songToLoad];
          finalIndex = 0;
        }
      }
      
      setQueue(finalQueue);
      // 🔴 CORREÇÃO 1: loadSong NÃO deve setar currentIndex quando chamado diretamente
      // Mas quando section === null (deep link), precisa setar para 0
      if (section === null) {
        setCurrentIndex(0);
      } else {
        // Quando há seção, o índice será gerenciado pela função que chamou loadSong
        // ou será calculado pela própria loadSong quando necessário
        setCurrentIndex(finalIndex);
      }

      // O resto da função continua...

      // TODO: Implementar notificação de sucesso sem toast

      // OTIMIZADO: Reutiliza o elemento de áudio já criado
      if (!audioRef.current) {
        console.error('[Player] AudioRef não encontrado, tentando recriar...');
        // Se por algum motivo não existe, usa o que já foi criado no useEffect inicial
        return;
      }
      
      const audio = audioRef.current;
      const newSrc = formatStorageUrl(songToLoad.audio_url);
      
      audio.pause();
      audio.currentTime = 0;
      audio.src = '';
      audio.load();

      // 🔧 CORREÇÃO CRÍTICA: Promise que estava causando loading infinito
      await new Promise<void>((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          audio.removeEventListener('canplaythrough', onCanPlayThrough);
          audio.removeEventListener('error', onError);
          reject(new Error('Timeout ao carregar áudio'));
        }, 8000); // 8 segundos de timeout
        
        const onCanPlayThrough = () => {
          clearTimeout(timeoutId);
          audio.removeEventListener('canplaythrough', onCanPlayThrough);
          audio.removeEventListener('error', onError);
          resolve(); // ✅ ADICIONADO: resolve() que estava faltando
        };
        
        const onError = (e: Event) => {
          clearTimeout(timeoutId);
          audio.removeEventListener('canplaythrough', onCanPlayThrough);
          audio.removeEventListener('error', onError);
          reject(new Error('Erro ao carregar áudio')); // ✅ ADICIONADO: reject() que estava faltando
        };
        
        audio.addEventListener('canplaythrough', onCanPlayThrough, { once: true });
        audio.addEventListener('error', onError, { once: true });
      });

      // Agora tenta tocar o áudio
      try {
        await audio.play();
        setIsPlaying(true);
        updatePlayerState('playing', 'loadSong-success');
        
        playerDebugLog('loadSong-success', {
          songId: songToLoad.id,
          currentIndex: finalIndex,
          queueLength: finalQueue.length,
          duration: performance.now() - startTime
        });
      } catch (playError) {
        console.error('[loadSong] Erro ao reproduzir:', playError);
        setIsPlaying(false);
        updatePlayerState('error', 'loadSong-play-failed');
        
        playerDebugLog('loadSong-play-failed', {
          songId: songToLoad.id,
          error: playError instanceof Error ? playError.message : 'Erro desconhecido',
          duration: performance.now() - startTime
        });
      }
      
    } catch (error) {
      // TODO: Implementar notificação de erro sem toast 
      setIsPlaying(false);
      
      playerDebugLog('loadSong-error', {
        songId,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
        duration: performance.now() - startTime
      });
    } finally {
      clearTimeout(loadingTimeout);
      resetLoadingState();
    }
  }, [handleLoadedMetadata, handleTimeUpdate, playNextSongRef, fadeOut, isShuffled, originalQueue, currentPlaylistName, user, fetchSongs, shuffleArray, queue, allSongs, isLoadingSong, lastProcessedSongId, currentSong, playerState.status, currentIndex]);

  // 🚀 OTIMIZAÇÃO CRÍTICA: Função para carregar playlist com cache global - EVITA CONFLITOS DE ESTADO
  const loadPlaylist = useCallback(async (
    songIds: number[],
    section: 'category' | 'journey' | 'favorites',
    category?: string,
    journeyStageId?: number | string | null,
    initialIndex?: number,
    source?: string
  ) => {
    playerDebugLog('loadPlaylist', {
      songIdsCount: songIds?.length,
      section,
      category,
      journeyStageId,
      initialIndex,
      source,
      currentSongId: currentSong?.id,
      currentIndex,
      queueLength: queue.length,
      status: playerState.status
    });

    // 🔧 CORREÇÃO: Validação prévia dos parâmetros
    if (!songIds || songIds.length === 0) {
      playerLog('[PLAYLIST] Lista de músicas vazia ou inválida');
      playerDebugLog('loadPlaylist', { error: 'lista de músicas vazia ou inválida' });
      return;
    }

    // 🆕 CORREÇÃO: Forçar reset quando trocar de categoria, EXCETO quando source === 'daily-verse' ou 'user-click'
    let adjustedInitialIndex = initialIndex;
    if (section === 'category' && category && category !== currentCategory && source !== 'daily-verse' && source !== 'user-click') {
      playerLog('[PLAYLIST] Categoria diferente detectada, forçando índice inicial = 0');
      playerDebugLog('loadPlaylist', { 
        oldCategory: currentCategory, 
        newCategory: category,
        forcedIndex: 0 
      });
      adjustedInitialIndex = 0;
    } else if (source === 'daily-verse') {
      // Quando source === 'daily-verse', respeita o initialIndex informado
      playerLog('[PLAYLIST] Origem: Versículo do Dia - respeitando índice inicial informado');
      playerDebugLog('loadPlaylist', { 
        source: 'daily-verse',
        initialIndex,
        category 
      });
    } else if (source === 'user-click' && section === 'category' && category && category !== currentCategory) {
      // Quando source === 'user-click' e categoria mudou, respeita o initialIndex informado (clique explícito do usuário)
      playerLog('[PLAYLIST] Categoria mudou mas origem é clique do usuário - respeitando índice inicial informado');
      playerDebugLog('loadPlaylist', { 
        source: 'user-click',
        oldCategory: currentCategory,
        newCategory: category,
        initialIndex,
        reason: 'category changed but user-click respected'
      });
    }

    // 🔧 CORREÇÃO: Validação do índice inicial
    const targetIndex = typeof adjustedInitialIndex === 'number' && adjustedInitialIndex >= 0 && adjustedInitialIndex < songIds.length 
      ? adjustedInitialIndex 
      : 0;

    // 🔧 CORREÇÃO: Identificar a música alvo ANTES de qualquer processamento
    const expectedSongId = Number(songIds[targetIndex]);
    if (!expectedSongId || isNaN(expectedSongId)) {
      playerLog('[PLAYLIST] ID de música alvo inválido');
      return;
    }

    // Atualiza ref para detector de inconsistência
    lastRequestedSongIdRef.current = expectedSongId;

    // 🆕 CORREÇÃO: Se está em buffering, força switching-context primeiro
    if (playerState.status === 'buffering') {
      playerLog('[PLAYLIST] Forçando switching-context para trocar música durante buffering');
      updatePlayerState('switching-context', 'loadPlaylist-force-switch', { 
        isUserInteraction: true 
      });
      // Pequeno delay para garantir que a transição seja processada
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    // 🆕 NOVO: Verifica se pode carregar playlist
    if (!canPerformAction('play-playlist')) {
      playerLog('[PLAYLIST] Não é possível carregar playlist no estado atual');
      playerDebugLog('loadPlaylist', { error: 'ação não permitida', status: playerState.status });
      return;
    }

    if (isLoadingPlaylist) {
      playerLog('[PLAYLIST] Já está carregando playlist, ignorando nova solicitação');
      playerDebugLog('loadPlaylist', { error: 'já carregando playlist' });
      return;
    }

    // 🆕 NOVO: Salva estado do shuffle antes de carregar nova playlist
    const wasShuffled = isShuffled;
    const currentSongId = currentSong?.id;

    // 🆕 NOVO: Atualiza estado unificado
    updatePlayerState('loading-playlist', 'loadPlaylist-start', { 
      isNetworkRequest: true,
      isUserInteraction: true 
    });

    // Cria chave de cache baseada na playlist
    const cacheKey = createPlaylistCacheKey(
      section, 
      category, 
      user?.id, 
      user?.tipo_de_conta
    );
    
    // Verifica cache primeiro
    const cachedPlaylist = cacheService.getSync<{
      songs: Song[];
      metadata: any;
    }>(cacheKey);
    
    if (cachedPlaylist && cachedPlaylist.songs && cachedPlaylist.songs.length > 0) {
      // 🆕 NOVO: Atualiza estado para cache hit
      updatePlayerState('transitioning', 'loadPlaylist-cache-hit', { isCacheOperation: true });
      
      // Usa dados do cache
      let songsToLoad = cachedPlaylist.songs;
      
      // 🔧 CORREÇÃO CRÍTICA: Validar se a música alvo existe no cache
      const targetSong = songsToLoad.find(s => Number(s.id) === expectedSongId);
      if (!targetSong) {
        // Música alvo não encontrada no cache, recarregando
        // Força recarregamento se música não estiver no cache
      } else {
        // 🔧 CORREÇÃO CRÍTICA: Recalcular índice real da música no cache
        const realIndex = songsToLoad.findIndex(s => Number(s.id) === expectedSongId);
        const finalIndex = realIndex !== -1 ? realIndex : 0;

        // 🚀 OTIMIZAÇÃO CRÍTICA: Atualizar todos os estados de uma vez para evitar conflitos
        // 🔴 CORREÇÃO 1: loadPlaylist define currentIndex UMA ÚNICA VEZ no início
        setQueue(songsToLoad);
        setCurrentIndex(finalIndex);
        setCurrentSong(songsToLoad[finalIndex]);
        setCurrentSection(section);
        setCurrentCategory(category);
        
        if (section === 'journey' && journeyStageId) {
          setCurrentJourneyStageId(journeyStageId);
        } else {
          setCurrentJourneyStageId(null);
        }
        
        if (section === 'favorites') {
          setCurrentPlaylistName('Minhas Músicas Favoritas');
        } else {
          setCurrentPlaylistName('');
        }

        // Se estava em modo shuffle antes, re-embaralha a nova playlist
        // ⚠️ IMPORTANTE: Aplica shuffle APENAS na queue recém carregada, não usa allSongs
        if (wasShuffled) {
          // Salva a nova queue como originalQueue antes de embaralhar
          setOriginalQueue([...songsToLoad]);
          
            // Aplica shuffle na nova queue, mantendo a música no finalIndex como primeira
            const currentSongInQueue = songsToLoad[finalIndex];
            if (currentSongInQueue) {
              const otherSongs = songsToLoad.filter((_, idx) => idx !== finalIndex);
              const shuffledOtherSongs = shuffleArray(otherSongs);
              const newShuffledQueue = [currentSongInQueue, ...shuffledOtherSongs];
              
              setQueue(newShuffledQueue);
              setCurrentIndex(0);
            }
        }

        // 🚀 OTIMIZAÇÃO: Configurar áudio de forma mais robusta
        if (audioRef.current && songsToLoad[finalIndex]) {
          const audio = audioRef.current;
          const newSrc = formatStorageUrl(songsToLoad[finalIndex].audio_url);
          
          // 🔧 CORREÇÃO: Parar áudio anterior antes de configurar novo
          audio.pause();
          audio.currentTime = 0;
          
          if (audio.src !== newSrc) {
            audio.src = newSrc;
            audio.load();
          }
          audio.volume = 1;
          
          // 🆕 NOVO: Atualiza estado para buffering
          updatePlayerState('buffering', 'loadPlaylist-audio-setup');
          
          // ✅ CORREÇÃO: Usa listeners de eventos em vez de verificar readyState imediatamente
          const onCanPlayThrough = () => {
            audio.removeEventListener('canplaythrough', onCanPlayThrough);
            audio.removeEventListener('error', onError);
            
            audio.play().then(() => {
              setIsPlaying(true);
              updatePlayerState('playing', 'loadPlaylist-cache-success');
              playerDebugLog('loadPlaylist-cache-success', {
                songId: songsToLoad[finalIndex].id,
                currentIndex: finalIndex,
                queueLength: songsToLoad.length
              });
            }).catch((playError) => {
              setIsPlaying(false);
              updatePlayerState('error', 'loadPlaylist-cache-play-failed');
              playerDebugLog('loadPlaylist-cache-play-failed', {
                songId: songsToLoad[finalIndex].id,
                error: playError instanceof Error ? playError.message : 'Erro desconhecido'
              });
            });
          };
          
          const onError = (e: Event) => {
            audio.removeEventListener('canplaythrough', onCanPlayThrough);
            audio.removeEventListener('error', onError);
            console.error('[loadPlaylist] Erro no áudio:', e);
            setIsPlaying(false);
            updatePlayerState('error', 'loadPlaylist-cache-audio-error');
            playerDebugLog('loadPlaylist-cache-audio-error', {
              songId: songsToLoad[finalIndex].id,
              error: 'erro no áudio'
            });
          };
          
          audio.addEventListener('canplaythrough', onCanPlayThrough, { once: true });
          audio.addEventListener('error', onError, { once: true });
        }
        
        return; // Sai da função usando cache
      }
    }

    setIsLoadingPlaylist(true);
    
    try {
      // 🆕 NOVO: Atualiza estado para transição
      updatePlayerState('transitioning', 'loadPlaylist-network-start', { isNetworkRequest: true });
      
      // Busca músicas
      const songs = await fetchSongs(songIds);
      if (!songs) {
        throw new Error('Músicas não encontradas');
      }

      // Ordena as músicas conforme a ordem de songIds
      const songsMap = new Map(songs.map(song => [Number(song.id), formatSong(song)]));
      let songsToLoad = songIds.map(id => songsMap.get(Number(id))).filter(Boolean) as Song[];
      
      // 🔧 CORREÇÃO CRÍTICA: Validar se a música alvo existe nos dados carregados
      const targetSong = songsToLoad.find(s => Number(s.id) === expectedSongId);
      if (!targetSong) {
        throw new Error(`Música alvo (ID: ${expectedSongId}) não encontrada nos dados carregados`);
      }

      // VALIDAÇÃO PREMIUM específica por seção
      if (section === 'journey' && user?.tipo_de_conta !== 'premium') {
        // JORNADA: apenas IDs 22, 23, 453 são gratuitos
        songsToLoad = filterJourneySongs(songsToLoad, user?.tipo_de_conta);
      } else if (section !== 'favorites' && user?.tipo_de_conta !== 'premium') {
        // OUTRAS SEÇÕES: usa validação normal
        songsToLoad = songsToLoad.filter(s => canPlaySong(s, user));
      }

      // Armazena no cache
      const playlistMetadata = {
        section,
        category,
        journeyStageId,
        userType: user?.tipo_de_conta,
        songsCount: songsToLoad.length,
        originalSongsCount: songs.length
      };
      
      // Armazena no cache com TTL
      cacheService.set(cacheKey, {
        songs: songsToLoad,
        metadata: playlistMetadata
      }, PLAYLIST_CACHE_TTL).catch(() => {
        // Ignora erros assíncronos
      });

      // 🔧 CORREÇÃO CRÍTICA: Recalcular índice após filtros
      const realIndex = songsToLoad.findIndex(s => Number(s.id) === expectedSongId);
      const finalIndex = realIndex !== -1 ? realIndex : 0;

      // 🚀 OTIMIZAÇÃO CRÍTICA: Atualizar todos os estados de uma vez para evitar conflitos
      // 🔴 CORREÇÃO 1: loadPlaylist define currentIndex UMA ÚNICA VEZ no início
      setQueue(songsToLoad);
      setCurrentIndex(finalIndex);
      setCurrentSong(songsToLoad[finalIndex]);
      setCurrentSection(section);
      setCurrentCategory(category);
      if (section === 'journey' && journeyStageId) {
        setCurrentJourneyStageId(journeyStageId);
      } else {
        setCurrentJourneyStageId(null);
      }
      if (section === 'favorites') {
        setCurrentPlaylistName('Minhas Músicas Favoritas');
      } else {
        setCurrentPlaylistName('');
      }

      // Se estava em modo shuffle antes, re-embaralha a nova playlist
      // ⚠️ IMPORTANTE: Aplica shuffle APENAS na queue recém carregada, não usa allSongs
      if (wasShuffled) {
        // Salva a nova queue como originalQueue antes de embaralhar
        setOriginalQueue([...songsToLoad]);
        
          // Aplica shuffle na nova queue, mantendo a música no finalIndex como primeira
          const currentSongInQueue = songsToLoad[finalIndex];
          if (currentSongInQueue) {
            const otherSongs = songsToLoad.filter((_, idx) => idx !== finalIndex);
            const shuffledOtherSongs = shuffleArray(otherSongs);
            const newShuffledQueue = [currentSongInQueue, ...shuffledOtherSongs];
            
            setQueue(newShuffledQueue);
            setCurrentIndex(0);
          }
      }

      // 🚀 OTIMIZAÇÃO: Configurar áudio de forma mais robusta
      if (audioRef.current && songsToLoad[finalIndex]) {
        const audio = audioRef.current;
        const newSrc = formatStorageUrl(songsToLoad[finalIndex].audio_url);
        
        // 🔧 CORREÇÃO: Parar áudio anterior antes de configurar novo
        audio.pause();
        audio.currentTime = 0;
        
        if (audio.src !== newSrc) {
          audio.src = newSrc;
          audio.load();
        }
        audio.volume = 1;
        
        // 🆕 NOVO: Atualiza estado para buffering
        updatePlayerState('buffering', 'loadPlaylist-network-audio-setup');
        
        // ✅ CORREÇÃO: Usa listeners de eventos em vez de verificar readyState imediatamente
        const onCanPlayThrough = () => {
          audio.removeEventListener('canplaythrough', onCanPlayThrough);
          audio.removeEventListener('error', onError);
          
          audio.play().then(() => {
            setIsPlaying(true);
            updatePlayerState('playing', 'loadPlaylist-network-success');
            playerDebugLog('loadPlaylist-network-success', {
              songId: songsToLoad[finalIndex].id,
              currentIndex: finalIndex,
              queueLength: songsToLoad.length
            });
          }).catch((playError) => {
            setIsPlaying(false);
            updatePlayerState('error', 'loadPlaylist-network-play-failed');
            playerDebugLog('loadPlaylist-network-play-failed', {
              songId: songsToLoad[finalIndex].id,
              error: playError instanceof Error ? playError.message : 'Erro desconhecido'
            });
          });
        };
        
        const onError = (e: Event) => {
          audio.removeEventListener('canplaythrough', onCanPlayThrough);
          audio.removeEventListener('error', onError);
          console.error('[loadPlaylist] Erro no áudio:', e);
          setIsPlaying(false);
          updatePlayerState('error', 'loadPlaylist-network-audio-error');
          playerDebugLog('loadPlaylist-network-audio-error', {
            songId: songsToLoad[finalIndex].id,
            error: 'erro no áudio'
          });
        };
        
        audio.addEventListener('canplaythrough', onCanPlayThrough, { once: true });
        audio.addEventListener('error', onError, { once: true });
      }
    } catch (error) {
      console.error('[PLAYLIST] Erro ao carregar playlist:', error);
      updatePlayerState('error', 'loadPlaylist-error');
      playerDebugLog('loadPlaylist-error', {
        error: error instanceof Error ? error.message : 'Erro desconhecido',
        songIdsCount: songIds.length
      });
    } finally {
      setIsLoadingPlaylist(false);
    }
  }, [
    canPerformAction,
    playerState.status,
    updatePlayerState,
    isLoadingPlaylist,
    user,
    fetchSongs,
    isShuffled,
    currentSong?.id,
    reshuffleQueueFromIndex,
    currentIndex,
    queue.length,
    currentCategory
  ]);


  // Inicializa o elemento de áudio - ULTRA OTIMIZADO PARA VELOCIDADE
  useEffect(() => {
    const audio = new Audio();
    audio.preload = 'none'; // 🚀 OTIMIZAÇÃO: Não carrega nada inicialmente
    audio.volume = 1;
    audio.crossOrigin = 'anonymous';
    audioRef.current = audio;

    // Configurar todos os event listeners uma única vez
    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      setProgress((audio.currentTime / audio.duration) * 100);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    // Event listeners principais
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', () => {
      playerDebugLog('audio-ended', {
        songId: currentSongRef.current?.id,
        currentIndex: currentIndexRef.current,
        queueLength: queueRef.current.length,
        duration: audio.duration
      });
      endedHandler();
    });
    audio.addEventListener('play', () => {
      playerDebugLog('audio-play', {
        songId: currentSongRef.current?.id,
        currentIndex: currentIndexRef.current,
        queueLength: queueRef.current.length,
        currentTime: audio.currentTime,
        duration: audio.duration,
        readyState: audio.readyState
      });
      setIsPlaying(true);
    });
    audio.addEventListener('pause', () => {
      playerDebugLog('audio-pause', {
        songId: currentSongRef.current?.id,
        currentIndex: currentIndexRef.current,
        queueLength: queueRef.current.length,
        currentTime: audio.currentTime,
        duration: audio.duration
      });
      setIsPlaying(false);
    });
    audio.addEventListener('stalled', () => {
      playerDebugLog('audio-stalled', {
        songId: currentSongRef.current?.id,
        currentIndex: currentIndexRef.current,
        queueLength: queueRef.current.length,
        currentTime: audio.currentTime,
        duration: audio.duration,
        readyState: audio.readyState
      });
    });
    audio.addEventListener('waiting', () => {
      playerDebugLog('audio-waiting', {
        songId: currentSongRef.current?.id,
        currentIndex: currentIndexRef.current,
        queueLength: queueRef.current.length,
        currentTime: audio.currentTime,
        duration: audio.duration,
        readyState: audio.readyState
      });
    });
    audio.addEventListener('canplay', () => {
      playerDebugLog('audio-canplay', {
        songId: currentSongRef.current?.id,
        currentIndex: currentIndexRef.current,
        queueLength: queueRef.current.length,
        currentTime: audio.currentTime,
        duration: audio.duration,
        readyState: audio.readyState
      });
    });
    audio.addEventListener('canplaythrough', () => {
      playerDebugLog('audio-canplaythrough', {
        songId: currentSongRef.current?.id,
        currentIndex: currentIndexRef.current,
        queueLength: queueRef.current.length,
        currentTime: audio.currentTime,
        duration: audio.duration,
        readyState: audio.readyState
      });
    });
    audio.addEventListener('error', (e) => {
      playerDebugLog('audio-error', {
        songId: currentSongRef.current?.id,
        currentIndex: currentIndexRef.current,
        queueLength: queueRef.current.length,
        currentTime: audio.currentTime,
        duration: audio.duration,
        readyState: audio.readyState,
        error: audio.error ? `${audio.error.code}: ${audio.error.message}` : 'erro desconhecido'
      });
    });

    return () => {
      // Limpeza completa
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', endedHandler);
      audio.removeEventListener('play', () => setIsPlaying(true));
      audio.removeEventListener('pause', () => setIsPlaying(false));
      audio.removeEventListener('stalled', () => {});
      audio.removeEventListener('waiting', () => {});
      audio.removeEventListener('canplay', () => {});
      audio.removeEventListener('canplaythrough', () => {});
      audio.removeEventListener('error', () => {});
      
      audio.pause();
      audio.src = '';
      audio.load();
    };
  }, [endedHandler]);

  // Effect to stop music when user logs out
  useEffect(() => {
    if (prevUserRef.current && !user) {
      // Usuário fez logout (tinha usuário e agora não tem)
      stop();
    }
    
    prevUserRef.current = user;
  }, [user, stop]);

  // Função para buscar posição específica no áudio
  const seek = (time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
    }
  };

  // Função para alternar modo aleatório
  const toggleShuffle = () => {
    if (currentSection === 'journey' || location.pathname.includes('/caminhada')) {
      showToast('O modo aleatório não está disponível durante a Jornada Bíblica', 'error');
      return;
    }
    
    setIsShuffled(prev => !prev);
    showToast(
      !isShuffled 
        ? 'Reprodução aleatória ativada' 
        : 'Reprodução aleatória desativada',
      'info'
    );
  };

  // 🔧 CORREÇÃO: Implementa lógica de shuffle quando isShuffled muda
  // ⚠️ IMPORTANTE: Este efeito roda APENAS quando isShuffled muda, usando refs para valores atuais
  useEffect(() => {
    // Usa refs para obter valores atuais sem depender deles no useEffect
    const currentQueue = queueRef.current;
    const currentIdx = currentIndexRef.current;
    const currentSongRef = currentQueue[currentIdx];
    
    if (currentQueue.length === 0) {
      return;
    }

    if (isShuffled) {
      // Ativar shuffle: salva queue original e embaralha APENAS a queue atual
      if (originalQueue.length === 0) {
        // Salva a queue original para poder restaurar depois
        const beforeIds = currentQueue.map(s => s.id);
        setOriginalQueue([...currentQueue]);
        
        // Embaralha APENAS a queue atual, mantendo a música atual como primeira
        const currentSongInQueue = currentSongRef;
        const oldIndex = currentIdx;
        
        if (!currentSongInQueue) {
          return;
        }
        
        // Remove a música atual da queue e embaralha o resto
        const otherSongs = currentQueue.filter(song => song.id !== currentSongInQueue.id);
        const shuffledOtherSongs = shuffleArray(otherSongs);
        
        const newShuffledQueue = [currentSongInQueue, ...shuffledOtherSongs];
        
        setQueue(newShuffledQueue);
        setCurrentIndex(0); // A música atual agora está na posição 0
      }
    } else {
      // Desativar shuffle: restaura queue original
      if (originalQueue.length > 0) {
        const currentSongId = currentSongRef?.id;
        const oldIndex = currentIdx;
        
        const originalIndex = originalQueue.findIndex(song => song.id === currentSongId);
        const restoredIndex = originalIndex >= 0 ? originalIndex : 0;
        
        setQueue([...originalQueue]);
        setCurrentIndex(restoredIndex);
        setOriginalQueue([]);
      }
    }
  }, [isShuffled, originalQueue.length]); // ⚠️ DEPENDE APENAS DE isShuffled e originalQueue.length

  // 🆕 NOVO: Controle de visibilidade do MiniPlayer
  useEffect(() => {
    if (currentSong && !isTransitioning && !isSwitchingSong) {
      setShowMiniPlayer(true);
    }
  }, [currentSong, isTransitioning, isSwitchingSong]);

  // 🆕 NOVO: Atualiza estado de favorito quando a música muda
  useEffect(() => {
    const updateFavoriteState = async () => {
      if (currentSong?.id && user?.id) {
        const isFav = await isSongFavorited(Number(currentSong.id), user.id);
        setIsFavorited(isFav);
      } else {
        setIsFavorited(false);
      }
    };
    updateFavoriteState();
  }, [currentSong?.id, user?.id]);


  // Retorna o contexto com todas as propriedades
  const contextValue = useMemo(() => ({
    currentSong,
    isPlaying,
    currentTime,
    duration,
    queue,
    currentIndex,
    currentSection,
    currentCategory,
    currentPlaylistName,
    currentJourneyStageId,
    isShuffled,
    originalQueue,
    isSwitchingSong: isSwitchingSongUnified,
    isTransitioning: isTransitioningUnified,
    isFromHomePage,
    isFavorited,
    isNavigatingToPlayer: isNavigatingToPlayerUnified,
    showMiniPlayer,
    setIsFavorited,
    setCurrentSong,
    setIsPlaying,
    setCurrentTime,
    setDuration,
    setQueue,
    setCurrentIndex,
    setCurrentSection,
    setCurrentCategory,
    setCurrentPlaylistName,
    setCurrentJourneyStageId,
    setIsShuffled,
    setOriginalQueue,
    setIsSwitchingSong: (value) => updatePlayerState(value ? 'switching-context' : 'idle', 'switch-song'),
    setIsTransitioning: (value) => updatePlayerState(value ? 'transitioning' : 'idle', 'switch-song'),
    resetPlayerState,
    play,
    pause,
    seek,
    playNextSong,
    playPreviousSong,
    toggleShuffle,
    togglePlayPause,
    loadSong,
    loadPlaylist,
    stop,
    canPerformAction,
    playerState,
    emergencyReset
  }), [
    currentSong,
    isPlaying,
    currentTime,
    duration,
    queue,
    currentIndex,
    currentSection,
    currentCategory,
    currentPlaylistName,
    currentJourneyStageId,
    isShuffled,
    originalQueue,
    isSwitchingSongUnified,
    isTransitioningUnified,
    isFromHomePage,
    isFavorited,
    isNavigatingToPlayerUnified,
    showMiniPlayer,
    setIsFavorited,
    setCurrentSong,
    setIsPlaying,
    setCurrentTime,
    setDuration,
    setQueue,
    setCurrentIndex,
    setCurrentSection,
    setCurrentCategory,
    setCurrentPlaylistName,
    setCurrentJourneyStageId,
    setIsShuffled,
    setOriginalQueue,
    updatePlayerState,
    resetPlayerState,
    play,
    pause,
    seek,
    playNextSong,
    playPreviousSong,
    toggleShuffle,
    togglePlayPause,
    loadSong,
    loadPlaylist,
    stop,
    canPerformAction,
    playerState,
    emergencyReset
  ]);

  return (
    <GlobalPlayerContext.Provider value={contextValue}>
      {children}
    </GlobalPlayerContext.Provider>
  );
};

// Componente público que fornece o contexto do player
export const GlobalPlayerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <GlobalPlayerContextProvider>
      {children}
    </GlobalPlayerContextProvider>
  );
};

export const useGlobalPlayer = () => {
  const context = useContext(GlobalPlayerContext);
  if (!context) {
    throw new Error('useGlobalPlayer must be used within a GlobalPlayerProvider');
  }
  return context;
}; 